import { EventEmitter } from 'eventemitter3';
import type {
	MessageData,
	StreamCallbacks,
	StreamChunkData,
	StreamEndData,
	StreamErrorData,
	StreamStartData,
	TranscriptionData,
} from '../../types/message.types';

/**
 * Message Service Event Map
 */
export type MessageServiceEventMap = {
	message: (message: MessageData) => void;
	'stream-start': (data: StreamStartData) => void;
	'stream-chunk': (data: StreamChunkData) => void;
	'stream-end': (data: StreamEndData) => void;
	'stream-error': (data: StreamErrorData) => void;
	'conversational-chunk': (text: string) => void;
	'conversational-complete': (text: string) => void;
	action: (action: any) => void;
	'action-executed': (result: any) => void;
	transcription: (data: TranscriptionData) => void;
	error: (error: any) => void;
};

/**
 * Message State
 */
export interface MessageState {
	currentSessionId: string | null;
	currentMessageId: string | null;
	isStreaming: boolean;
	messageHistory: MessageData[];
	activeCallbacks: StreamCallbacks | null;
}

/**
 * Service that handles message processing, streaming, and communication.
 * Manages text messages, stream events, and transcriptions.
 */
export class MessageService extends EventEmitter<MessageServiceEventMap> {
	private static instance: MessageService | null = null;
	private instanceId: number;
	private instanceNumber: number;

	// Message state
	private state: MessageState = {
		currentSessionId: null,
		currentMessageId: null,
		isStreaming: false,
		messageHistory: [],
		activeCallbacks: null,
	};

	// Stream buffers
	private conversationalBuffer: string = '';
	private actionResultBuffer: string = '';

	private constructor(instanceNumber: number) {
		super();
		this.instanceNumber = instanceNumber;
		this.instanceId = instanceNumber; // Keep for backward compatibility
	}

	/**
	 * Get singleton instance
	 */
	public static getInstance(): MessageService {
		if (!MessageService.instance) {
			const instanceNumber = 1;
			MessageService.instance = new MessageService(instanceNumber);
		}
		return MessageService.instance;
	}

	/**
	 * Set active stream callbacks
	 */
	public setActiveCallbacks(callbacks: StreamCallbacks | null): void {
		this.state.activeCallbacks = callbacks;
	}

	/**
	 * Get active callbacks
	 */
	public getActiveCallbacks(): StreamCallbacks | null {
		return this.state.activeCallbacks;
	}

	/**
	 * Set current session ID
	 */
	public setSessionId(sessionId: string | null): void {
		this.state.currentSessionId = sessionId;
	}

	/**
	 * Get current session ID
	 */
	public getSessionId(): string | null {
		return this.state.currentSessionId;
	}

	/**
	 * Set current message ID
	 */
	public setCurrentMessageId(messageId: string | null): void {
		this.state.currentMessageId = messageId;
	}

	/**
	 * Get current message ID
	 */
	public getCurrentMessageId(): string | null {
		return this.state.currentMessageId;
	}

	/**
	 * Add message to history
	 */
	public addMessage(message: MessageData): void {
		this.state.messageHistory.push(message);
		// Keep only last 100 messages to prevent memory issues
		if (this.state.messageHistory.length > 100) {
			this.state.messageHistory = this.state.messageHistory.slice(-100);
		}
		this.emit('message', message);
	}

	/**
	 * Handle incoming message
	 */
	public handleMessage(data: any): void {
		const callbacks = this.state.activeCallbacks;

		// Call message callback if provided
		callbacks?.onMessage?.(data);

		// Don't emit message event if this is part of a voice stream
		const isVoiceStreamMessage =
			data.id && this.state.currentMessageId === data.id;

		// Add to history and emit event if not a voice stream message
		if (data.role && data.content && !isVoiceStreamMessage) {
			const message: MessageData = {
				id: data.id || `msg-${Date.now()}`,
				role: data.role,
				content: data.content,
				conversationalResponse: data.conversationalResponse,
				timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
				metadata: data.metadata,
			};

			this.addMessage(message);
		}
	}

	/**
	 * Handle stream start
	 */
	public handleStreamStart(data: any): void {
		this.state.isStreaming = true;
		this.conversationalBuffer = '';

		const streamData: StreamStartData = {
			messageId: data.messageId || 0,
			sessionId: data.sessionId || this.state.currentSessionId || '',
		};

		this.emit('stream-start', streamData);
	}

	/**
	 * Handle stream chunk
	 */
	public handleStreamChunk(data: any): void {
		if (data.text) {
			const chunkData: StreamChunkData = {
				text: data.text,
				messageId: data.messageId || 0,
			};

			this.emit('stream-chunk', chunkData);
		}
	}

	/**
	 * Handle stream end
	 */
	public handleStreamEnd(data: any): void {
		this.state.isStreaming = false;

		if (data.fullText) {
			const endData: StreamEndData = {
				fullText: data.fullText,
				messageId: data.messageId || 0,
			};

			this.emit('stream-end', endData);
		}
	}

	/**
	 * Handle stream event (for conversational streaming)
	 */
	public handleStreamEvent(data: any): void {
		const event = data.event;
		const callbacks = this.state.activeCallbacks;
		if (!callbacks) return;

		switch (event.type) {
			case 'conversational':
				if (event.complete) {
					callbacks.onConversationalComplete?.(event.content);
					this.emit('conversational-complete', event.content);
					this.conversationalBuffer = '';
				} else {
					this.conversationalBuffer += event.content;
					callbacks.onConversationalChunk?.(event.content);
					this.emit('conversational-chunk', event.content);
				}
				break;

			case 'action':
				callbacks.onAction?.(event.content);
				this.emit('action', event.content);
				break;

			case 'complete':
				// Streaming complete - clear active callbacks
				this.state.activeCallbacks = null;
				this.state.isStreaming = false;
				break;
		}
	}

	/**
	 * Handle action executed
	 */
	public handleActionExecuted(data: any): void {
		const callbacks = this.state.activeCallbacks;
		callbacks?.onExecution?.(data.result);
		this.emit('action-executed', data.result);
	}

	/**
	 * Handle action result stream
	 */
	public handleActionResultStream(data: any): void {
		const event = data.event;
		const callbacks = this.state.activeCallbacks;
		if (!callbacks) return;

		switch (event.type) {
			case 'conversational':
				if (event.complete) {
					callbacks.onConversationalComplete?.(event.content);
					this.actionResultBuffer = '';
				} else {
					this.actionResultBuffer += event.content;
					callbacks.onConversationalChunk?.(event.content);
				}
				break;

			case 'complete':
				// Action result interpretation complete
				break;
		}
	}

	/**
	 * Handle transcription
	 */
	public handleTranscription(data: any): void {
		if (data.text) {
			const transcriptionData: TranscriptionData = {
				text: data.text,
				isFinal: data.isFinal || false,
				confidence: data.confidence,
				timestamp: data.timestamp,
			};

			const callbacks = this.state.activeCallbacks;
			callbacks?.onUserVoiceText?.(data.text, data.isFinal || false);

			this.emit('transcription', transcriptionData);
		}
	}

	/**
	 * Handle stream error
	 */
	public handleStreamError(data: any): void {
		const callbacks = this.state.activeCallbacks;
		const error = data.error || 'Stream processing error';

		callbacks?.onError?.(error);

		const errorData: StreamErrorData = {
			error,
			messageId: data.messageId,
		};

		this.emit('stream-error', errorData);
		this.emit('error', {
			type: 'stream-error',
			message: error,
			details: data,
			timestamp: Date.now(),
		});

		// Clear streaming state on error
		this.state.isStreaming = false;
	}

	/**
	 * Handle general error
	 */
	public handleError(data: any): void {
		const callbacks = this.state.activeCallbacks;
		const error = data.message || 'Connection error';

		callbacks?.onError?.(error);

		this.emit('error', {
			type: 'websocket-error',
			message: error,
			details: data,
			timestamp: Date.now(),
		});
	}

	/**
	 * Clear message state
	 */
	public clearState(): void {
		this.state.activeCallbacks = null;
		this.state.isStreaming = false;
		this.conversationalBuffer = '';
		this.actionResultBuffer = '';
	}

	/**
	 * Subscribe to an event with automatic cleanup
	 * Returns an unsubscribe function
	 */
	public subscribe<K extends keyof MessageServiceEventMap>(
		event: K,
		handler: MessageServiceEventMap[K],
	): () => void {
		// eventemitter3 only resolves its ArgumentMap for concrete event keys, so
		// it can't correlate a generic key with the matching handler union — the
		// event map guarantees the pairing, so assert the listener type it expects.
		const listener = handler as EventEmitter.EventListener<
			MessageServiceEventMap,
			K
		>;

		// Add the event listener
		this.on(event, listener);

		// Return unsubscribe function
		return () => {
			this.off(event, listener);
		};
	}

	/**
	 * Reset service completely (enhanced for Reset Pattern)
	 */
	public reset(): void {
		// 1. CLEAR ACTIVE STATE
		this.clearState();

		// 2. CLEAR SESSION DATA
		this.state.currentSessionId = null;
		this.state.currentMessageId = null;

		// 3. CLEAR MESSAGE HISTORY
		this.state.messageHistory = [];

		// 4. CLEAR EVENT LISTENERS
		this.removeAllListeners();
	}

	/**
	 * Check if the service is currently in use
	 */
	public isInUse(): boolean {
		return (
			this.state.isStreaming ||
			this.state.activeCallbacks !== null ||
			this.conversationalBuffer.length > 0 ||
			this.actionResultBuffer.length > 0
		);
	}

	/**
	 * Destroy the singleton instance (only for app unmount)
	 */
	public static destroy(): void {
		if (MessageService.instance) {
			MessageService.instance.reset();
			MessageService.instance = null;
		}
	}

	/**
	 * Get message history
	 */
	public getMessageHistory(): MessageData[] {
		return [...this.state.messageHistory];
	}

	/**
	 * Check if currently streaming
	 */
	public isStreaming(): boolean {
		return this.state.isStreaming;
	}
}
